
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { FormValuesEdit } from "../../types";
import { UseFormReturn } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { NumberInput } from "../form-fields/NumberInput";
import { FormSelect } from "../form-fields/FormSelect";
import { TemplateSelector } from "../form-fields/TemplateSelector";
import { useEffect } from "react";

interface LaunchFormProps {
  form: UseFormReturn<FormValuesEdit>;
  isLoading: boolean;
  formMappings: { id: number; jotform_form_id: string; form_title: string | null }[] | undefined;
  clients: { id: number; nombre: string }[] | undefined;
  templates: { id: number; name: string }[] | undefined;
  onSubmit: (data: FormValuesEdit) => void;
  onClose: () => void;
}

export function LaunchForm({
  form,
  isLoading,
  formMappings,
  clients,
  templates,
  onSubmit,
  onClose
}: LaunchFormProps) {
  useEffect(() => {
  }, [form.formState.errors]);

  return (
    <div className="space-y-6 w-full min-w-0 max-w-full pt-2">
        <FormSelect
          form={form}
          name="form_mapping_id"
          label="Formulario"
          placeholder="Seleccionar formulario"
          options={formMappings?.map(mapping => ({
            id: mapping.id.toString(),
            name: mapping.form_title || mapping.jotform_form_id
          })) || []}
        />

        <FormSelect
          form={form}
          name="id_cliente"
          label="Cliente"
          placeholder="Seleccionar cliente"
          options={clients?.map(client => ({ id: client.id.toString(), name: client.nombre })) || []}
        />

        <TemplateSelector
          form={form}
          templates={templates}
        />

        <FormSelect
          form={form}
          name="generacion_reporte"
          label="Momento de Generación del Reporte"
          placeholder="Seleccionar momento de generación"
          options={[
            { id: "individual", name: "Individual (tras respuesta individual)" },
            { id: "equipo", name: "Equipo (tras completar equipo)" },
            { id: "individual_equipo", name: "Individual + Equipo (tras completar ambos)" }
          ]}
        />

        <FormSelect
          form={form}
          name="report_type"
          label="Tipo de Reporte"
          placeholder="Seleccionar tipo de reporte"
          options={[
            { id: "individual", name: "Individual (solo variables individuales)" },
            { id: "equipo", name: "Equipo (solo variables de equipo)" },
            { id: "individual_equipo", name: "Individual + Equipo (ambos tipos de variables)" }
          ]}
        />

        <FormSelect
          form={form}
          name="email_sending_type"
          label="Envío de Email"
          placeholder="Seleccionar criterio de envío"
          options={[
            { id: "tras_generacion", name: "Tras generación del reporte" },
            { id: "manual", name: "Manual" }
          ]}
        />

        <NumberInput
          form={form}
          name="max_respuestas"
          label="Máximo de respuestas"
          placeholder="Ingrese máximo de respuestas"
        />

        <NumberInput
          form={form}
          name="report_lifetime"
          label="Tiempo de vida del reporte (días)"
          placeholder="Ingrese tiempo de vida en días"
        />

        <FormField
          control={form.control}
          name="expiration_date"
          render={({ field }) => (
            <FormItem className="flex flex-col w-full">
              <FormLabel>Fecha de expiración</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full pl-3 text-left font-normal",
                        !field.value && "text-muted-foreground"
                      )}
                    >
                      {field.value ? (
                        format(field.value, "dd/MM/yyyy")
                      ) : (
                        <span>Seleccione una fecha</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={field.onChange}
                    disabled={(date) => date < new Date()}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Auto-generation configuration section */}
        <Separator className="my-4" />
        <div className="space-y-4">
          <h4 className="text-sm font-medium">Generación Automática de Reportes</h4>

          <FormField
            control={form.control}
            name="auto_generate_report"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <FormLabel>Auto-generar reportes</FormLabel>
                  <FormDescription className="text-xs">
                    Genera reportes automáticamente al recibir submissions
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {form.watch("auto_generate_report") && (
            <>
              <FormField
                control={form.control}
                name="template_referral_id"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Plantilla para Referidos (Opcional)</FormLabel>
                    <Select
                      value={field.value?.toString() || ""}
                      onValueChange={(value) => field.onChange(value ? parseInt(value) : undefined)}
                    >
                      <SelectTrigger className="w-full min-w-0">
                        <SelectValue placeholder="Ninguna (solo individuales)" className="truncate" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Ninguna</SelectItem>
                        {templates?.map((template) => (
                          <SelectItem key={template.id} value={template.id.toString()}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription className="text-xs">
                      Plantilla usada para reportes cuando se completen los referidos
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="notify_on_auto_generate"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>Notificar al participante</FormLabel>
                      <FormDescription className="text-xs">
                        Enviar email al completar la generación automática
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </>
          )}
        </div>
    </div>
  );
}
